// findReferencedRelationsForRange — Resolver helper for "what tables are
// explicitly mentioned by a FROM / JOIN / UPDATE / DELETE FROM / INSERT INTO /
// MERGE INTO clause?", including unaliased bare targets (which
// computeAliasBindingsForRange deliberately does NOT emit, since it returns
// only entries that have an explicit alias).
//
// Used by post-filters that need a "narrow" set of tables in the current
// statement — e.g. the per-engine completion-item filters (PG/MySQL/Oracle/
// SQLite). Pure function, no tree-sitter; reuses readQualifiedName +
// tokenSkip so it correctly handles every quote form, comments, strings, and
// nested parentheses.
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Migrate pgCompletion off
// regex → Resolver. Shared with the upcoming mysql/oracle/sqlite migration.

import type { ParseOptions, QualifiedName } from '../../sql-grammar/model';
import type { ParsedBuffer } from '../types';
import { skipContextFor, trySkipNonCode, type SkipContext } from '../classify/tokenSkip';

import { readIdentifier, readQualifiedName, isBareIdentStart } from './identifiers';
import { skipParen } from './aliasScope.scanner';

const TARGET_KEYWORDS: ReadonlySet<string> = new Set([
  'FROM', 'JOIN', 'STRAIGHT_JOIN', 'INTO', 'UPDATE', 'USING',
]);

const RESETTERS: ReadonlySet<string> = new Set([
  'WHERE', 'GROUP', 'ORDER', 'HAVING', 'LIMIT', 'OFFSET', 'FETCH',
  'UNION', 'INTERSECT', 'EXCEPT', 'RETURNING', 'SET', 'VALUES',
  'ON', 'WHEN', 'WINDOW',
  'SELECT', 'WITH', 'INSERT', 'DELETE', 'MERGE',
]);

export function findReferencedRelationsForRange(
  buffer: ParsedBuffer,
  start: number,
  end: number,
): readonly QualifiedName[] {
  const { source, options } = buffer.parsedDocument;
  return scanReferencedRelations(source, options, start, end);
}

// eslint-disable-next-line complexity, max-lines-per-function
function scanReferencedRelations(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): QualifiedName[] {
  const ctx: SkipContext = skipContextFor(options);
  const out: QualifiedName[] = [];
  const seen = new Set<string>();
  let expectTarget = false;
  let depth = 0;
  let i = start;

  const push = (qn: QualifiedName): void => {
    const key = `${qn.foldedSchema ?? ''}.${qn.foldedName}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(qn);
  };

  while (i < end) {
    const ident = readIdentifier(source, i, end, options);
    if (ident && (ident.quoted || isBareIdentStart(source, i, options.engine))) {
      if (depth > 0) { i = ident.end; continue; }
      const upper = ident.quoted ? null : ident.value.toUpperCase();
      if (upper !== null && TARGET_KEYWORDS.has(upper)) {
        expectTarget = true;
        i = ident.end; continue;
      }
      if (upper !== null && RESETTERS.has(upper)) {
        expectTarget = false;
        i = ident.end; continue;
      }
      if (expectTarget) {
        const rq = readQualifiedName(source, i, end, options);
        if (rq) {
          push(rq.qualified);
          expectTarget = false;
          i = rq.range.endOffset; continue;
        }
      }
      i = ident.end; continue;
    }

    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }

    const c = source[i];
    if (c === '(') {
      // FROM (SELECT …) AS x — derived-table body. Skip past the close paren
      // and reset the target expectation; the subquery's own targets are NOT
      // exposed at the outer level.
      if (depth === 0 && expectTarget) {
        i = skipParen(source, i, end, ctx);
        expectTarget = false; continue;
      }
      depth++; i++; continue;
    }
    if (c === ')') { if (depth > 0) depth--; i++; continue; }
    if (c === ',' && depth === 0) {
      // Old-style FROM list: `FROM a, b, c` — comma re-arms target expectation.
      // Conservatively rearm any time we see a top-level comma, the next
      // identifier-or-keyword decides whether it's a target or a resetter.
      expectTarget = true; i++; continue;
    }
    i++;
  }
  return out;
}
